import { useCallback, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import ListSubheader from '@mui/material/ListSubheader';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import MuiTextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/AddRounded';
import { styled } from '@mui/material/styles';
import Tooltip from '@components/Tooltip';
import { useTranslation } from '@hooks';
import { Typography } from '@components/Typography';
import { usePopupZIndex } from '@components/Modal/popupLayer';

export const CssTextFieldOutlined = styled(MuiTextField)({
    '&.MuiFormControl-root': {
        fontFamily: 'Rubik, sans-serif',
        fontSize: '0.875rem',
        paddingBottom: 16,
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderRadius: 8,
            borderColor: 'var(--divider)',
            legend: {
                letterSpacing: '0.6px',
            },
        },
        '& .Mui-disabled+fieldset': {
            borderColor: 'var(--divider)',
        },
        '& input': {
            padding: '12px',
            height: '1rem',
            lineHeight: '16px',
            fontSize: '14px',
            fontFamily: 'Rubik, sans-serif',
            borderColor: 'var(--divider)',
            color: 'var(--text-primary)',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'var(--primary-main)',
        },
        '& .MuiSelect-icon': {
            color: 'rgba(0, 0, 0, 0.88)',
        },
        '& .MuiSelect-icon.Mui-disabled': {
            color: 'var(--text-tertiary)',
        },
        '& .MuiSelect-select': {
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 12,
            boxSizing: 'border-box',
            fontSize: '0.875rem',
            fontFamily: 'Rubik, sans-serif',
            lineHeight: '16px',
        },
        '&.Mui-error': {
            '.MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--error-main)',
            },
        },
    },
    '.MuiFormLabel-root': {
        fontSize: '0.875rem',
        fontFamily: 'Rubik, sans-serif',
        lineHeight: '1rem',
        letterSpacing: '0.5px',
        transform: 'translate(12px, 12px) scale(1)',
        '&.Mui-focused': {
            color: 'var(--primary-main)',
        },
        '&.Mui-error': {
            color: 'var(--error-main)',
        },
        '& .MuiFormLabel-asterisk': {
            color: 'var(--error-main)',
        },
    },
    '.MuiFormHelperText-root': {
        position: 'absolute',
        bottom: -3,
        left: 0,
        margin: '0 12px',
        fontFamily: 'Rubik, sans-serif',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '14px',
        letterSpacing: '0.4px',
        maxWidth: 'calc(100% - 24px)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&.Mui-error': {
            color: 'var(--error-main)',
        },
    },
    '.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.8)',
    },
});

const menuItemTextStyles = {
    padding: '8px 18px',
    width: '100%',
    color: 'rgba(0, 0, 0, 0.88)',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '14px',
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    '@media (max-width: 1024px)': {
        minHeight: '32px',
    },
};

export const CssMenuItem = styled(MenuItem)({
    '&.MuiMenuItem-root': {
        ...menuItemTextStyles,
        '&:hover': {
            cursor: 'pointer',
        },
        '&.Mui-selected': {
            backgroundColor: '#E4EAEB',
        },
        '&.Mui-disabled': {
            pointerEvents: 'auto',
            '&:active': {
                pointerEvents: 'none',
            },
        },
    },
});

export const CssNoOptions = styled(CssMenuItem)({
    pointerEvents: 'none',
    '&:hover': {
        cursor: 'default',
        backgroundColor: 'transparent',
    },
});

export const CssNoOptionsText = styled('div')(({ theme }) => ({
    ...theme.typography.body1,
    boxSizing: 'border-box',
    pointerEvents: 'none',
    ...menuItemTextStyles,
}));

const CssListSubheader = styled(ListSubheader)({
    lineHeight: '32px !important',
    fontFamily: 'Rubik',
    fontWeight: '500 !important',
    fontSize: '10px !important',
    letterSpacing: '1.5px !important',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
});

export const CssMenuItemCreatable = styled(CssMenuItem)(() => {
    return {
        '&.MuiMenuItem-root': {
            padding: '12px 18px 12px 9px',
            display: 'flex',
            alignItems: 'center',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            marginTop: 8,
            '& svg': {
                height: 16,
                '& path': {
                    fill: 'var(--primary-main)',
                },
            },
            '@media (max-width: 1024px)': {
                minHeight: '32px',
            },
        },
    };
});

export const Select = ({
    className,
    options,
    style,
    dropdownStyles,
    withEndAdornment = false,
    groupBased = false,
    onClickEndAdornment = () => {},
    creatable = false,
    creatableText = '',
    creatableClick = () => {},
    InputProps,
    inputProps,
    slotProps,
    ...rest
}) => {
    const { t } = useTranslation();
    const popupZIndex = usePopupZIndex();
    const isMobile = useMediaQuery('@media (max-width: 1024px)');
    const [open, setOpen] = useState(false);

    const hasOneGroup = groupBased && options.length === 1;

    const renderGroupedOptions = useCallback(
        (option, index) => {
            const items = option.children.map(option =>
                option.tooltip ? (
                    <Tooltip key={option.value} title={option.tooltip} placement="top">
                        <CssMenuItem key={option.value} value={option.value} disabled={option.disabled} sx={option.sx}>
                            {option.label}
                        </CssMenuItem>
                    </Tooltip>
                ) : (
                    <CssMenuItem key={option.value} value={option.value} disabled={option.disabled} sx={option.sx}>
                        {option.label}
                    </CssMenuItem>
                ),
            );
            return hasOneGroup
                ? items
                : [
                      <CssListSubheader disableSticky key={index}>
                          {option.group}
                      </CssListSubheader>,
                      items,
                  ];
        },
        [options, hasOneGroup],
    );

    return (
        <Box noValidate autoComplete="off" className={className} style={style}>
            <CssTextFieldOutlined
                select
                fullWidth
                slotProps={{
                    ...slotProps,
                    select: {
                        open,
                        onOpen: () => setOpen(true),
                        onClose: () => setOpen(false),
                        MenuProps: {
                            style: { zIndex: popupZIndex },
                            slotProps: {
                                paper: {
                                    ...(creatable
                                        ? {
                                              sx: {
                                                  '& .MuiList-root': {
                                                      paddingBottom: 0,
                                                  },
                                              },
                                          }
                                        : {}),
                                    style: {
                                        maxWidth: '280px',
                                        maxHeight: isMobile ? '35vh' : '55vh',
                                        ...dropdownStyles,
                                    },
                                },
                            },
                        },
                    },
                    formHelperText: { title: typeof rest.helperText === 'string' ? rest.helperText : undefined },
                    input: {
                        endAdornment: withEndAdornment ? (
                            <InputAdornment position="end" sx={{ marginRight: '18px' }}>
                                <IconButton
                                    disabled={rest.disabled}
                                    sx={{
                                        padding: '2px',
                                        '& svg path': {
                                            fill: rest.disabled ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                                        },
                                    }}
                                    onClick={onClickEndAdornment}
                                >
                                    <CloseIcon sx={{ height: '18px', width: '18px' }} />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                        ...InputProps,
                    },
                    htmlInput: inputProps,
                }}
                {...rest}
            >
                {options?.length ? (
                    groupBased ? (
                        options.map(renderGroupedOptions)
                    ) : (
                        options.map(option =>
                            option.tooltip ? (
                                <Tooltip key={option.value} title={option.tooltip} placement="top">
                                    <CssMenuItem
                                        key={option.value}
                                        value={option.value}
                                        disabled={option.disabled}
                                        sx={option.sx}
                                    >
                                        {option.label}
                                    </CssMenuItem>
                                </Tooltip>
                            ) : (
                                <CssMenuItem
                                    key={option.value}
                                    value={option.value}
                                    disabled={option.disabled}
                                    sx={option.sx}
                                >
                                    {option.label}
                                </CssMenuItem>
                            ),
                        )
                    )
                ) : (
                    <CssNoOptions>{t('common.noOptions')}</CssNoOptions>
                )}
                {creatable ? (
                    <CssMenuItemCreatable onClick={creatableClick} value="create-new">
                        <AddIcon />
                        <Typography sx={{ color: 'var(--primary-main)' }} variant="body2">
                            {creatableText}
                        </Typography>
                    </CssMenuItemCreatable>
                ) : null}
            </CssTextFieldOutlined>
        </Box>
    );
};
